import {
    call, fork, join, put, select, takeLatest,
} from 'redux-saga/effects';
import { Task } from 'redux-saga';
import { ContentLanguagesDefinition } from 'common/services/i18nService';
import { hasCURAccessForAction } from 'common/services/permissions/commonPermissionService';
import { FeatureTypesWithCurs } from 'common/services/permissions/typings';
import { ContextState } from './reducer';
import { ContextTypeKeys } from './types';
import { getAssessment, setQuestionSetSupportedLanguages } from './actions';
import { RootState } from '../rootReducer';
import { ContentEditorState } from '../../features/contentEditor/reducer';
import { resetEditorLoaded } from '../../features/contentEditor/actions';
import apiService from '../../services/apiService';
import storeManager from '..';

const getContextState = (state: RootState): ContextState => state.context;

export function* persistInitialQuestionSetLanguages(assessmentData: any) {
    const context: ContextState = yield select(getContextState);
    const { currentItemContext } = context;
    const shownLanguages = (currentItemContext?.supportedLanguages || []) as ContentLanguagesDefinition[];
    const canEdit = hasCURAccessForAction(FeatureTypesWithCurs.CONTENT_BLOCK_EDIT)
        && !context.contentEditStatus?.isReadOnlyMetadata;
    if (
        !canEdit
        || String(currentItemContext?.objectId) !== String(assessmentData?.contentItemId)
        || assessmentData.isGenericEmptyQuestionSet
        || assessmentData.supportedLanguages?.length
        || shownLanguages.length === 0
    ) {
        return;
    }
    yield put(setQuestionSetSupportedLanguages(shownLanguages));
}

function* saveQuestionSetSupportedLanguages() {
    const context: ContextState = yield select(getContextState);
    const { assessmentData } = context;
    if (!assessmentData?.contentItemId) {
        return;
    }
    const updatedAssessmentData = {
        ...assessmentData,
        supportedLanguages: context.currentItemContext?.supportedLanguages || [],
    };
    const formData = {
        id: assessmentData.contentItemId,
        lastModified: assessmentData.lastModified,
        context: 'author',
        // eslint-disable-next-line @typescript-eslint/camelcase
        status: (assessmentData as any).assessment_status,
        jsonString: JSON.stringify(updatedAssessmentData),
    };
    try {
        const response = yield call(
            apiService.updateAssessmentData,
            String(assessmentData.contentItemId),
            formData
        );
        if (response) {
            yield put(getAssessment(String(assessmentData.contentItemId)));

            // The editor iframe keeps the copy of the question set it loaded and saves that copy back
            // with every question save it makes, so reload it to load the languages just saved.
            const featureId = 'feature_contentEditor_author_edit_2_0';
            const editorState = (storeManager.getState() as RootState)[featureId] as ContentEditorState | undefined;
            const contentWindow = editorState?.iframeTarget?.contentWindow;
            if (contentWindow) {
                yield put(resetEditorLoaded(featureId));
                contentWindow.location.reload();
            }
        }
    } catch (err) {
        console.error('saveQuestionSetSupportedLanguages failed', err);
    }
}

// Tracks the in-flight question-set language save task so other save flows (which write to
// the same assessment's EditAssessment endpoint) can wait for it to actually finish instead
// of racing it — see waitForQuestionSetLanguageSave.
let questionSetLanguageSaveTask: Task | undefined;

export function* waitForQuestionSetLanguageSave() {
    if (questionSetLanguageSaveTask?.isRunning()) {
        yield join(questionSetLanguageSaveTask);
    }
}

export function* watchQuestionSetSupportedLanguages() {
    yield takeLatest(ContextTypeKeys.SET_QUESTION_SET_SUPPORTED_LANGUAGES, function* watchedSave() {
        questionSetLanguageSaveTask = yield fork(saveQuestionSetSupportedLanguages);
    });
}
